import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Flex, DatePicker } from "antd"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"

import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./RangeSelector.module.css"

RangeSelector.propTypes = {
  params: PropTypes.shape({
    type: PropTypes.string,
    overview_range: PropTypes.string,
    date_gteq: PropTypes.string,
    date_lteq: PropTypes.string
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onChangeDateRange: PropTypes.func
}

function RangeSelector({ params, onChangeFilters, onChangeDateRange }) {
  const { t } = useTranslation()
  const { overview_range, date_gteq, date_lteq } = params
  const activeRange = overview_range || "current_month"
  const isCustomRange = activeRange === "total_by_scope"

  const todayMaxDate = useMemo(() => dayjs().endOf("day"), [])
  const appliedRangeRef = useRef(null)

  const setFilterTarget = useCallback(
    (name, value) => onChangeFilters({ target: { name, value } }),
    [onChangeFilters]
  )

  const setDateRange = useCallback(
    (gteq, lteq) => {
      if (onChangeDateRange) {
        onChangeDateRange({ date_gteq: gteq, date_lteq: lteq })
      } else {
        setFilterTarget("date_gteq", gteq)
        setFilterTarget("date_lteq", lteq)
      }
    },
    [onChangeDateRange, setFilterTarget]
  )

  useEffect(() => {
    if (!overview_range) setFilterTarget("overview_range", "current_month")
  }, [overview_range, setFilterTarget])

  useEffect(() => {
    if (isCustomRange) return
    if (appliedRangeRef.current === activeRange) return
    appliedRangeRef.current = activeRange
    let start, end
    if (activeRange === "current_month") {
      start = dayjs().startOf("month")
      end = dayjs().endOf("month")
    } else if (activeRange === "last_month") {
      start = dayjs().subtract(1, "month").startOf("month")
      end = dayjs().subtract(1, "month").endOf("month")
    } else if (activeRange === "last_30_days") {
      start = dayjs().subtract(30, "days").startOf("day")
      end = dayjs().endOf("day")
    }
    if (start && end) {
      setDateRange(start.format(DATE_FORMAT), end.format(DATE_FORMAT))
    }
  }, [activeRange, isCustomRange, setDateRange])

  useEffect(() => {
    if (!isCustomRange) return
    const defaultStart = dayjs(date_lteq || dayjs()).startOf("month").format(DATE_FORMAT)
    const defaultEnd = dayjs().format(DATE_FORMAT)
    if (!date_gteq && !date_lteq) {
      setDateRange(defaultStart, defaultEnd)
    } else {
      if (!date_gteq) setFilterTarget("date_gteq", defaultStart)
      if (!date_lteq) setFilterTarget("date_lteq", defaultEnd)
    }
  }, [isCustomRange]) // eslint-disable-line react-hooks/exhaustive-deps

  const [rangePickerOpen, setRangePickerOpen] = useState(false)

  const handleDateRangeChange = (dates) => {
    setRangePickerOpen(false)
    if (dates?.length === 2) {
      const [start, end] = dates
      setDateRange(start.format(DATE_FORMAT), end.format(DATE_FORMAT))
    } else {
      const start = dayjs().startOf("month")
      const end = dayjs().endOf("month")
      setDateRange(start.format(DATE_FORMAT), end.format(DATE_FORMAT))
    }
  }

  const dateRangeValue = date_gteq && date_lteq ? [dayjs(date_gteq), dayjs(date_lteq)] : null

  const formattedDateRange = dateRangeValue
    ? `${dateRangeValue[0].format("MMM D")} - ${dateRangeValue[1].format("MMM D YYYY")}`
    : t("accounting.range_selector.select_date_range")

  return (
    <Flex align="center" gap={8} justify="end" wrap className={styles.rangeSelector}>
      {isCustomRange && (
        <div className={styles.rangePickerWrapper}>
          <button type="button" className={styles.rangePreview} onClick={() => setRangePickerOpen((open) => !open)}>
            <span className={styles.rangePreviewText}>{formattedDateRange}</span>
          </button>
          <DatePicker.RangePicker
            open={rangePickerOpen}
            onOpenChange={setRangePickerOpen}
            value={dateRangeValue}
            defaultPickerValue={dateRangeValue || [dayjs(), dayjs().add(1, "month")]}
            onChange={handleDateRangeChange}
            format={DATE_FORMAT}
            disabledDate={(current) => current && current.valueOf() > todayMaxDate.valueOf()}
            maxDate={todayMaxDate}
            className={styles.hiddenRangePicker}
            dropdownClassName={styles.rangePopup}
            // popupStyle={{ transform: "translateY(1.8rem)" }}
          />
        </div>
      )}
    </Flex>
  )
}

export default memo(RangeSelector)
